// src/components/leitner/TasksView.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { 
  ClipboardList, Plus, Clock, Users, Globe, Lock, Calendar,
  Play, ChevronRight, Trophy, Target, Search, Filter, Share2,
  CheckCircle, AlertCircle, Timer, Eye, BarChart3, Edit, Trash2
} from 'lucide-react';
import { taskService } from '../../services/taskService';
import { leitnerService } from '../../services/leitnerService';
import { useAuth } from '../../contexts/AuthContext';
import TaskCreatorManager from './TaskCreatorManager';
import TaskTakerView from './TaskTakerView';
import TaskShareModal from './TaskShareModal';
import TaskPerformanceView from './TaskPerformanceView';

const TasksView = ({ bookId, questions, styling, isOwner, bookData }) => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // 'list', 'create', 'edit', 'take', 'performance'
  const [selectedTask, setSelectedTask] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all'); // 'all', 'available', 'completed'
  const [showShareModal, setShowShareModal] = useState(false);
  const [taskToShare, setTaskToShare] = useState(null);
  const [taskProgress, setTaskProgress] = useState({});
  const [performanceTask, setPerformanceTask] = useState(null);
  const [allNotes, setAllNotes] = useState([]);

  const fetchTasks = useCallback(async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      let fetchedTasks;
      
      if (isOwner) {
        fetchedTasks = await taskService.getBookTasks(bookId);
      } else {
        fetchedTasks = await taskService.getAvailableTasks(bookId, user.email, user.uid);
      }
      
      // Fetch progress for each task - handle individual failures gracefully
      const progressMap = {};
      await Promise.all(fetchedTasks.map(async (task) => {
        try {
          const progress = await taskService.getTaskProgress(bookId, task.id, user.uid);
          if (progress) {
            progressMap[task.id] = progress;
          }
        } catch (err) {
          console.warn(`Failed to fetch progress for task ${task.id}:`, err);
          // Continue with other tasks
        }
      }));
      
      setTaskProgress(progressMap);
      setTasks(fetchedTasks);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }, [bookId, user, isOwner]);



  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // Load notes for section-based tasks
  useEffect(() => {
    const loadNotes = async () => {
      if (!bookId) return;
      try {
        const notesData = await leitnerService.getLeitnerNotes(bookId);
        setAllNotes(notesData);
      } catch (error) {
        console.error('Failed to load notes:', error);
      }
    };
    loadNotes();
  }, [bookId]);

  const handleCreateTask = async (taskData) => {
    try {
      const newTask = await taskService.createTask(bookId, {
        ...taskData,
        questionIds: taskData.questionIds || []
      }, user.uid);
      setTasks(prev => [newTask, ...prev]);
      setView('list');
    } catch (error) {
      console.error('Failed to create task:', error);
    }
  };

  const handleUpdateTask = async (taskData) => {
    try {
      await taskService.updateTask(bookId, selectedTask.id, {
        ...taskData,
        questionIds: taskData.questionIds || []
      });
      setTasks(prev => prev.map(t => 
        t.id === selectedTask.id ? { ...t, ...taskData } : t
      ));
      setView('list');
      setSelectedTask(null);
    } catch (error) {
      console.error('Failed to update task:', error);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    
    try {
      await taskService.deleteTask(bookId, taskId);
      setTasks(prev => prev.filter(t => t.id !== taskId));
    } catch (error) {
      console.error('Failed to delete task:', error);
    }
  };

  const handleStartTask = async (task) => {
    // Initialize progress if not exists
    if (!taskProgress[task.id]) {
      await taskService.initializeTaskProgress(bookId, task.id, user.uid, task.questionIds);
    }
    setSelectedTask(task);
    setView('take');
  };

  const handleEditTask = (task) => {
    setSelectedTask(task);
    setView('edit');
  };

  const handleViewPerformance = (task) => {
    setPerformanceTask(task);
    setView('performance');
  };

  const handleShareTask = (task) => {
    setTaskToShare(task);
    setShowShareModal(true);
  };

  const handleTaskComplete = async (result) => {
    // Save Leitner progress
    await taskService.saveTaskAttempt(bookId, selectedTask.id, user.uid, result);
    
    // Save task result for analytics
    if (taskService.saveTaskResult) {
      try {
        await taskService.saveTaskResult(bookId, selectedTask.id, user.uid, {
          taskName: selectedTask.name,
          userEmail: user.email,
          score: result.correct || result.score || 0,
          totalQuestions: result.questionsStudied || result.totalQuestions || 0,
          totalMarks: result.correct || result.score || 0,
          maxMarks: result.questionsStudied || result.totalQuestions || 0,
          timeSpent: result.timeSpent || 0,
          questionResults: result.questionResults || {},
          attemptNumber: (taskProgress[selectedTask.id]?.attemptCount || 0) + 1
        });
      } catch (error) {
        console.error('Failed to save task result:', error);
      }
    }
    
    await fetchTasks();
    setView('list');
    setSelectedTask(null);
  };

  const getTaskStatus = (task) => {
    const progress = taskProgress[task.id];
    if (!progress) return 'new';
    
    const questionIds = task.questionIds || [];
    const completedCount = Object.entries(progress.questions || {})
      .filter(([id, q]) => q.box >= 4)
      .length;
    
    if (completedCount >= questionIds.length && questionIds.length > 0) return 'completed';
    if (completedCount > 0) return 'in-progress';
    return 'started';
  };

  const getProgressPercent = (task) => {
    const progress = taskProgress[task.id];
    if (!progress) return 0;
    
    const questionIds = task.questionIds || [];
    if (questionIds.length === 0) return 0;
    
    const totalBoxScore = Object.values(progress.questions || {})
      .reduce((sum, q) => sum + (q.box || 1), 0);
    const maxScore = questionIds.length * 4;
    
    return Math.round((totalBoxScore / maxScore) * 100);
  };

  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.name?.toLowerCase().includes(searchQuery.toLowerCase());
    const status = getTaskStatus(task);
    
    if (filter === 'available') return matchesSearch && status !== 'completed';
    if (filter === 'completed') return matchesSearch && status === 'completed';
    return matchesSearch;
  });

  const getVisibilityIcon = (visibility) => {
    switch (visibility) {
      case 'public': return <Globe className="h-4 w-4 text-green-400" />;
      case 'organisation': return <Users className="h-4 w-4 text-blue-400" />;
      default: return <Lock className="h-4 w-4 text-amber-400" />;
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = date?.toDate?.() || new Date(date);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // ==================== RENDER VIEWS ====================

  // Create Task View
  if (view === 'create') {
    return (
      <TaskCreatorManager
        bookId={bookId}
        questions={questions}
        task={null}
        onSave={handleCreateTask}
        onCancel={() => setView('list')}
      />
    );
  }

  // Edit Task View
  if (view === 'edit' && selectedTask) {
    return (
      <TaskCreatorManager
        bookId={bookId}
        questions={questions}
        task={selectedTask}
        onSave={handleUpdateTask}
        onCancel={() => { setView('list'); setSelectedTask(null); }}
      />
    );
  }

  // Take Task View
  if (view === 'take' && selectedTask) {
    const taskQuestions = selectedTask.questionIds
      .map(id => questions.find(q => q.id === id))
      .filter(Boolean);
    
    return (
      <TaskTakerView
        task={selectedTask}
        questions={taskQuestions}
        notes={allNotes}
        bookId={bookId}
        bookData={bookData}
        progress={taskProgress[selectedTask.id]}
        onComplete={handleTaskComplete}
        onBack={() => { setView('list'); setSelectedTask(null); }}
        styling={styling}
      />
    );
  }

  // Performance View
  if (view === 'performance' && performanceTask) {
    // Flatten questions same as TaskTakerView
    const taskQuestions = performanceTask.questionIds
      .map(id => questions.find(q => q.id === id))
      .filter(Boolean);
    
    const flattenedQuestions = [];
    taskQuestions.forEach(q => {
      if (q.type === 'simple' || !q.type) {
        flattenedQuestions.push({ ...q, isStem: false, text: q.text || q.questionText });
      } else if (q.type === 'stem' && q.subQuestions) {
        q.subQuestions.forEach((subQ, idx) => {
          flattenedQuestions.push({
            ...subQ,
            id: subQ.id || `${q.id}_sub_${idx}`,
            isStem: true,
            stemId: q.id,
            stemText: q.stemText,
            parentQuestionId: q.id,
            text: subQ.text
          });
        });
      }
    });

    return (
      <TaskPerformanceView
        bookId={bookId}
        task={performanceTask}
        questions={flattenedQuestions}  // Pass flattened, not raw
        styling={styling}
        viewMode={isOwner ? 'owner' : 'learner'}
        onBack={() => { setView('list'); setPerformanceTask(null); }}
      />
    );
  }

  // ==================== LIST VIEW ====================
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <ClipboardList className="h-7 w-7" style={{ color: styling.primaryColor }} />
            Leitner Quiz Tasks
          </h2>
          <p className="text-slate-400 text-sm mt-1">
            Complete tasks to master content through spaced repetition
          </p>
        </div>
        
        {isOwner && (
          <button
            onClick={() => setView('create')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-white font-medium transition-all hover:opacity-90"
            style={{ backgroundColor: styling.primaryColor }}
          >
            <Plus className="h-5 w-5" />
            Create Task
          </button>
        )}
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search tasks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>
        
        <div className="flex gap-2">
          {['all', 'available', 'completed'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all capitalize ${
                filter === f
                  ? 'text-white'
                  : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
              }`}
              style={filter === f ? { backgroundColor: styling.primaryColor } : {}}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* Tasks List */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin h-8 w-8 border-4 border-slate-600 border-t-transparent rounded-full"
               style={{ borderTopColor: styling.primaryColor }} />
        </div>
      ) : filteredTasks.length === 0 ? (
        <div className="text-center py-16 bg-slate-800 rounded-lg">
          <ClipboardList className="h-16 w-16 text-slate-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-slate-300 mb-2">
            {searchQuery ? 'No tasks found' : 'No tasks available'}
          </h3>
          <p className="text-slate-400 mb-6">
            {isOwner 
              ? 'Create your first task to start assigning learning activities'
              : 'Check back later for new leitner practice tasks'
            }
          </p>
          {isOwner && (
            <button
              onClick={() => setView('create')}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-white font-medium transition-all hover:opacity-90"
              style={{ backgroundColor: styling.primaryColor }}
            >
              <Plus className="h-5 w-5" />
              Create First Task
            </button>
          )}
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredTasks.map((task) => {
            const status = getTaskStatus(task);
            const progressPercent = getProgressPercent(task);
            
            return (
              <div
                key={task.id}
                className="bg-slate-800 rounded-lg p-5 hover:bg-slate-750 transition-all border border-slate-700"
              >
                <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                  {/* Task Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-3">
                      <div 
                        className="p-2 rounded-lg shrink-0"
                        style={{ backgroundColor: `${styling.primaryColor}40` }}
                      >
                        <Target className="h-5 w-5" style={{ color: styling.primaryColor }} />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-white text-lg truncate">
                            {task.name}
                          </h3>
                          {getVisibilityIcon(task.visibility)}
                          
                          {status === 'completed' && (
                            <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full flex items-center gap-1">
                              <CheckCircle className="h-3 w-3" />
                              Completed
                            </span>
                          )}
                          {status === 'in-progress' && (
                            <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full">
                              In Progress
                            </span>
                          )}
                        </div>
                        
                        {task.description && (
                          <p className="text-slate-400 text-sm mt-1 line-clamp-2">
                            {task.description}
                          </p>
                        )}
                        
                        {/* Meta Info */}
                        <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-slate-400">
                          <span className="flex items-center gap-1">
                            <ClipboardList className="h-4 w-4" />
                            {task.questionIds?.length || 0} questions
                            {task.sections?.filter(s => s.type === 'note').length > 0 && (
                              <span className="text-teal-400 ml-1">+ {task.sections.filter(s => s.type === 'note').length} notes</span>
                            )}
                          </span>
                          
                          {task.isTimeBound && (
                            <span className="flex items-center gap-1">
                              <Calendar className="h-4 w-4" />
                              {formatDate(task.startDate)} - {formatDate(task.endDate)}
                            </span>
                          )}
                          
                          {task.timeLimitPerQuestion > 0 && (
                            <span className="flex items-center gap-1">
                              <Timer className="h-4 w-4" />
                              {task.timeLimitPerQuestion}s per question
                            </span>
                          )}
                          
                          {task.passingScore && (
                            <span className="flex items-center gap-1">
                              <Trophy className="h-4 w-4" />
                              {task.passingScore}% to pass
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Progress Bar */}
                    {status !== 'new' && (
                      <div className="mt-4 ml-11">
                        <div className="flex items-center justify-between text-xs text-slate-400 mb-1">
                          <span>Mastery Progress</span>
                          <span>{progressPercent}%</span>
                        </div>
                        <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                          <div 
                            className="h-full rounded-full transition-all duration-300"
                            style={{ 
                              width: `${progressPercent}%`,
                              backgroundColor: styling.primaryColor 
                            }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2 lg:flex-col lg:items-stretch">
                    <button
                      onClick={() => handleStartTask(task)}
                      className="flex-1 lg:flex-none flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-white font-medium transition-all hover:opacity-90"
                      style={{ backgroundColor: styling.primaryColor }}
                    >
                      <Play className="h-4 w-4" />
                      {status === 'new' ? 'Start' : 'Continue'}
                    </button>
                    
                    <div className="flex gap-2">
                      {/* View Performance */}
                      <button
                        onClick={() => handleViewPerformance(task)}
                        className="p-2.5 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
                        title="View performance"
                      >
                        <BarChart3 className="h-4 w-4 text-slate-300" />
                      </button>
                      
                      {/* Share */}
                      <button
                        onClick={() => handleShareTask(task)}
                        className="p-2.5 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
                        title="Share task"
                      >
                        <Share2 className="h-4 w-4 text-slate-300" />
                      </button>
                      
                      {isOwner && (
                        <>
                          {/* Edit */}
                          <button
                            onClick={() => handleEditTask(task)}
                            className="p-2.5 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
                            title="Edit task"
                          >
                            <Edit className="h-4 w-4 text-slate-300" />
                          </button>
                          
                          {/* Delete */}
                          <button
                            onClick={() => handleDeleteTask(task.id)}
                            className="p-2.5 bg-red-500/20 hover:bg-red-500/30 rounded-lg transition-colors"
                            title="Delete task"
                          >
                            <Trash2 className="h-4 w-4 text-red-400" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && taskToShare && (
        <TaskShareModal
          task={taskToShare}
          bookId={bookId}
          bookData={bookData}
          onClose={() => { setShowShareModal(false); setTaskToShare(null); }}
          onUpdate={(updates) => {
            setTasks(prev => prev.map(t => 
              t.id === taskToShare.id ? { ...t, ...updates } : t
            ));
          }}
        />
      )}
    </div>
  );
};

export default TasksView;